<app-content-container
  [isLoading]="(patientContactsState$ | async)?.isLoading"
  [error]="(patientContactsState$ | async)?.error"
>
  <div class="flex align-items-center justify-content-between flex-wrap">
    <h2 class="my-1">Patient Details</h2>
    <div>
      <button
        *ngIf="!isEditMode"
        pButton
        type="button"
        label="Edit"
        icon="mi-round mi-edit"
        class="p-button-text p-button-sm mr-1"
        (click)="switchToEdit()"
        appClickEvent
        eventName="profile_edit_patient-details"
        data-cy="edit-patient-details-button"
      ></button>
      <button
        *ngIf="isEditMode"
        pButton
        type="button"
        label="Cancel Editing"
        icon="mi-round mi-close"
        [disabled]="isUpdatingPatientDetails$ | async"
        class="p-button-text p-button-sm mr-1"
        (click)="onCancelPatientDetailsForm()"
        appClickEvent
        eventName="profile_cancel_edit_patient-details"
        data-cy="cancel-editing-patient-details-button"
      ></button>
    </div>
  </div>
  <p-divider></p-divider>
  <div *ngIf="isEditMode && (isUpdatingPatientDetailsFinished$ | async) && (updatePatientDetailsError$ | async)">
    <app-message severity="error" [noMargin]="false" data-cy="update-patient-details-error-message">
      {{ getErrorMessage(updatePatientDetailsError$ | async) }}
    </app-message>
  </div>
  <div *ngIf="!isEditMode">
    <div class="grid">
      <div class="col-12 lg:col-4">
        <p class="text-black-400 p3 font-semibold mt-0 mb-1" data-cy="user-details-first-name-label">First Name</p>
        <p class="p3 my-0" data-cy="user-details-first-name-value">
          {{ (patient$ | async)?.firstName }}
        </p>
      </div>
      <div class="col-12 lg:col-8">
        <p class="text-black-400 p3 font-semibold mt-0 mb-1" data-cy="user-details-last-name-label">Last Name</p>
        <p class="p3 my-0" data-cy="user-details-last-name-value">
          {{ (patient$ | async)?.lastName }}
        </p>
      </div>
    </div>
    <div class="grid">
      <div class="col-12 lg:col-4">
        <p class="text-black-400 p3 font-semibold mt-3 mb-1" data-cy="user-details-email-label">Email Address</p>
        <p class="p3 my-0" data-cy="user-details-email-value">
          {{ (patientContactsState$ | async)?.data?.primaryEmail || 'Not Provided' }}
        </p>
      </div>
    </div>
    <div class="grid">
      <div class="col-12 lg:col-4">
        <p class="text-black-400 p3 font-semibold mt-3 mb-1" data-cy="user-details-primary-phone-label">
          Primary Phone Number
        </p>
        <p class="p3 my-0" data-cy="user-details-primary-phone-value">
          {{ ((patientContactsState$ | async)?.data?.primaryPhone?.phoneNumber | formatPhone) || 'Not Provided'
          }}<span
            *ngIf="(patientContactsState$ | async)?.data?.primaryPhone?.isMobile"
            class="text-black-400 ml-1"
            data-cy="mobile-tag"
            >(Mobile)</span
          >
        </p>
      </div>
      <div class="col-12 lg:col-8">
        <p class="text-black-400 p3 font-semibold mt-3 mb-1" data-cy="user-details-secondary-phone-label">
          Alternative Phone Number
        </p>
        <p class="p3 my-0" data-cy="user-details-secondary-phone-value">
          {{ ((patientContactsState$ | async)?.data?.secondaryPhone?.phoneNumber | formatPhone) || 'Not Provided'
          }}<span
            *ngIf="(patientContactsState$ | async)?.data?.secondaryPhone?.isMobile"
            class="text-black-400 ml-1"
            data-cy="mobile-tag"
            >(Mobile)</span
          >
        </p>
      </div>
    </div>
    <div class="mb-5">
      <p class="text-black-400 p3 font-semibold mt-3 mb-1">Communication Preferences</p>
      <p class="p3 my-0" data-cy="user-details-marketing-consent-value">
        {{ (patient$ | async)?.consentedToMarketing ? 'Opted in to' : 'Opted out of' }} receiving educational and
        promotional emails from CCS
      </p>
    </div>
    <div class="mb-5">
      <p class="text-black-400 p3 font-semibold mt-3 mb-1">Generic Medications</p>
      <p class="p3 my-0" data-cy="user-details-generic-medications-value">
        Generic medications {{ (patient$ | async)?.refusedGenericMedications ? 'refused' : 'allowed' }}
      </p>
    </div>
  </div>
  <div *ngIf="isEditMode">
    <form [formGroup]="patientDetailsForm" (ngSubmit)="onSubmitPatientDetailsForm()" data-cy="patient-details-form">
      <div class="grid grid-nogutter m-0">
        <app-form-field
          class="col-12 lg:col-4 lg:pr-3"
          [control]="asFormControl(patientDetailsForm.get('firstName'))"
          name="firstName"
          label="First Name"
          type="input"
        ></app-form-field>
        <app-form-field
          class="col-12 lg:col-4"
          [control]="asFormControl(patientDetailsForm.get('lastName'))"
          name="lastName"
          label="Last Name"
          type="input"
        ></app-form-field>
      </div>
      <div class="grid grid-nogutter m-0">
        <app-form-field
          class="col-12 lg:col-8"
          [control]="asFormControl(patientDetailsForm.get('primaryEmail'))"
          name="primaryEmail"
          label="Email Address"
          type="input"
        ></app-form-field>
      </div>
      <div class="grid grid-nogutter m-0">
        <div class="col-12 lg:col-4 lg:pr-3">
          <app-form-field
            [control]="asFormControl(patientDetailsForm.get('primaryPhoneNumber'))"
            name="primaryPhoneNumber"
            label="Primary Phone Number"
            type="input"
            inputMask="(999) 999-9999"
            inputMode="numeric"
            placeholder="(999) 999-9999"
            [noMargin]="true"
          ></app-form-field>
          <div class="mt-2">
            <app-form-field
              [control]="asFormControl(patientDetailsForm.get('isPrimaryPhoneMobile'))"
              name="isPrimaryPhoneMobile"
              type="checkbox-binary"
              label="Mobile?"
              class="mt-3"
            >
            </app-form-field>
          </div>
        </div>
        <div class="col-12 lg:col-4">
          <app-form-field
            [control]="asFormControl(patientDetailsForm.get('secondaryPhoneNumber'))"
            name="secondaryPhoneNumber"
            label="Secondary Phone Number"
            type="input"
            inputMask="(999) 999-9999"
            inputMode="numeric"
            placeholder="(999) 999-9999"
            [noMargin]="true"
          ></app-form-field>
          <div
            *ngIf="patientDetailsForm.errors?.['isPhoneDuplicate']"
            class="mt-1 mb-0 p4 text-error-600"
            data-cy="phone-duplicate-mismatch-error-message"
          >
            Secondary phone number cannot be the same as the primary one.
          </div>
          <div class="mt-2">
            <app-form-field
              [control]="asFormControl(patientDetailsForm.get('isSecondaryPhoneMobile'))"
              name="isSecondaryPhoneMobile"
              type="checkbox-binary"
              label="Mobile?"
            >
            </app-form-field>
          </div>
        </div>
      </div>

      <p class="p3 font-semibold mt-0 mb-3">Communication Preferences</p>
      <app-form-field
        [control]="asFormControl(patientDetailsForm.get('consentedToMarketing'))"
        name="consentedToMarketing"
        type="checkbox-binary"
        label="I'd like to receive educational and promotional emails from CCS"
      >
      </app-form-field>
      <p class="p3 font-semibold mt-0 mb-3">Generic Medications</p>
      <div class="flex align-items-start mb-5">
        <app-form-field
          [control]="asFormControl(patientDetailsForm.get('refusedGenericMedications'))"
          name="refusedGenericMedications"
          type="checkbox-binary"
        >
        </app-form-field>
        <label for="refusedGenericMedications" class="p-checkbox-label">
          To refuse generic medications, please check this box.
          <span class="font-semibold"
            >If allowed by your prescriber, we will dispense a generic equivalent unless you check this box</span
          >. Generic equivalents are usually less expensive than brand name drugs. If we dispense a brand drug, you may
          be responsible for a higher copayment and/or the difference between the brand and generic price of each drug.
        </label>
      </div>

      <button
        pButton
        [label]="(isUpdatingPatientDetails$ | async) ? 'Saving...' : 'Save'"
        type="submit"
        [disabled]="isUpdatingPatientDetails$ | async"
        data-cy="patient-details-form-submit-button"
        appClickEvent
        eventName="profile_submit_patient_details_changes"
      ></button>
    </form>
  </div>
</app-content-container>
